/**
 * PesticideFreshnessPlaque — unobtrusive "данные Госкаталога на {edition}"
 * caption for the pesticide catalog page + product card (AK-385 addendum).
 *
 * JTBD: the user must not mistake "мы давно не синхронизировались" for "препарат
 * исключён из реестра". A quiet muted line stating the applied edition + last
 * sync makes the catalog's provenance and freshness legible at a glance.
 *
 * DS: muted small text only; semantic tokens; no local colors. The stale hint
 * is a tiny amber dot (accent-brand) — informative, not alarming (the loud
 * "update available" alert lives in the admin panel, not here).
 */
import { formatRuDate } from '@/lib/dateFmt';
import { cn } from '@/lib/utils';
import { usePesticideSyncStatus } from '@/lib/pesticides/syncStatus';

interface Props {
  /** Probe the portal for a newer edition (default false — keep the user plaque cheap). */
  checkLatest?: boolean;
  className?: string;
}

export function PesticideFreshnessPlaque({
  checkLatest = false,
  className,
}: Props) {
  const { data, isLoading, isError } = usePesticideSyncStatus({ checkLatest });

  // Silent while loading or on error — a provenance caption should never draw
  // attention to its own failure; the catalog is still usable without it.
  if (isLoading || isError || !data || !data.sourceEdition) return null;

  const edition = formatRuDate(data.sourceEdition);
  const synced = data.syncedAt ? formatRuDate(data.syncedAt) : null;

  return (
    <p
      className={cn(
        'text-xs text-muted-foreground',
        className,
      )}
      data-testid="pesticide-freshness-plaque"
    >
      Данные Госкаталога Минсельхоза на {edition}
      {synced ? <> · синхронизировано {synced}</> : null}
      {data.isStale && data.latestAvailableEdition ? (
        <span
          className="ml-1.5 inline-flex items-center gap-1 text-accent-brand"
          title={`Доступна свежая редакция от ${formatRuDate(data.latestAvailableEdition)}`}
        >
          <span
            aria-hidden
            className="inline-block h-1.5 w-1.5 rounded-full bg-accent-brand"
          />
          доступно обновление
        </span>
      ) : null}
    </p>
  );
}
